import { useCallback, useEffect, useRef, useState } from "react";

import { ChevronLeft, ChevronRight, Close, ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  Paper,
  Typography,
} from "@mui/material";

import { UsersApiHelperForMember } from "../../api/ApiHelper";
import { formatPropertyPageValue, formatSnakeCaseToUserFriendly } from "../../utility/formatters";
import { MASK_NUMBER_UNFORMATTED, StatefulControlledTextField } from "../Core/StatefulControlledTextField";

// Properties to exclude from display in memory items
const EXCLUDED_PROPERTIES = new Set([
  "name",
  "Name",
  "object",
  "Object",
  "metadata",
  "documentId",
  "DocumentId",
  "documentType",
  "DocumentType",
  "lastUpdate",
  "LastUpdate",
  "originalExtension",
  "originalFilename",
  "id",
  "Id",
  "userId",
  "value",
]);

/**
 * Shared component for displaying chunk images with navigation and memory items
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Handler for closing the dialog
 * @param {string} imageUrl - Current image URL to display
 * @param {array} chunkUrls - Array of all chunk URLs for navigation
 * @param {number} currentIndex - Current index in the chunkUrls array
 * @param {function} onIndexChange - Handler for when the index changes
 * @param {boolean} showNavigation - Whether to show navigation buttons (default: true)
 * @param {string} documentId - Document ID for fetching memory items
 * @param {object} globalState - Global state for API calls
 */
const ChunkImageDialog = ({
  open,
  onClose,
  imageUrl,
  chunkUrls = [],
  currentIndex = 0,
  onIndexChange,
  showNavigation = true,
  documentId = null,
  globalState = null,
}) => {
  const [memoryItems, setMemoryItems] = useState([]);
  const [isLoadingMemory, setIsLoadingMemory] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [isEditingPageNumber, setIsEditingPageNumber] = useState(false);
  const imageContainerRef = useRef(null);
  const pageNumberInputRef = useRef(null);

  // Fetch memory items for the current chunk
  const fetchMemoryItems = useCallback(async () => {
    if (!documentId || !globalState || currentIndex < 0) {
      setMemoryItems([]);
      return;
    }

    try {
      setIsLoadingMemory(true);

      const params = {
        tenantID: import.meta.env.VITE_RGCHAT_TENANTID_TILLEY,
        userID: globalState?.producer_token || globalState?.agent_token,
        sessionID: "00000000-0000-0000-0000-000000000000",
        accessKey: "00000000-0000-0000-0000-000000000000",
        memoryName: "Documents",
        filterDocumentId: documentId,
        // producer_token + agent_token are required for the UsersAPI relay's
        // agent->producer UserGrant authorization.
        producer_token: globalState?.producer_token,
        agent_token: globalState?.agent_token,
      };

      const response = await UsersApiHelperForMember.http_get(
        `${import.meta.env.VITE_DATANAC_USERS_API_URL_BASE}/Memory/Item`,
        params
      );

      let results = [];
      const raw = response?.data;
      if (Array.isArray(raw)) {
        results = raw;
      } else if (typeof raw === "string") {
        const lines = raw.split("\n");
        const startIdx = lines.findIndex((line) => line.trim().startsWith("["));
        if (startIdx !== -1) {
          try {
            results = JSON.parse(lines.slice(startIdx).join("\n")) || [];
          } catch (parseErr) {
            console.warn("Failed to parse Memory/Item response:", parseErr);
            results = [];
          }
        }
      }

      // Filter by current chunk (currentIndex + 1 because chunks are 1-indexed)
      const currentChunkNumber = currentIndex + 1;
      const filteredItems = results.filter((item) => {
        // Check if item has _rg_chunk property that matches current chunk
        return item._rg_chunk === currentChunkNumber;
      });

      // Sort by _rg_ordinal if it exists (preserve API order otherwise)
      const sortedItems = [...filteredItems].sort((a, b) => {
        const ordinalA = Number(a._rg_ordinal) || 0;
        const ordinalB = Number(b._rg_ordinal) || 0;
        // If both have no ordinal, maintain original order
        if (ordinalA === 0 && ordinalB === 0) return 0;
        return ordinalA - ordinalB;
      });

      setMemoryItems(sortedItems);
    } catch (error) {
      console.error("Error fetching memory items:", error);
      setMemoryItems([]);
    } finally {
      setIsLoadingMemory(false);
    }
  }, [documentId, globalState, currentIndex]);

  // Fetch memory items when dialog opens or current index changes (debounced)
  useEffect(() => {
    if (!open || !documentId || !globalState) {
      return;
    }

    // Show loading state immediately
    setIsLoadingMemory(true);
    
    // Debounce the API call to avoid firing on rapid page changes
    const timeoutId = setTimeout(() => {
      fetchMemoryItems();
    }, 400); // Wait 400ms after last change before fetching

    // Cleanup: cancel the timeout if currentIndex changes again before it fires
    return () => clearTimeout(timeoutId);
  }, [open, documentId, globalState, currentIndex, fetchMemoryItems]);

  // Auto-focus and select text when entering edit mode
  useEffect(() => {
    if (isEditingPageNumber && pageNumberInputRef.current) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        pageNumberInputRef.current?.focus();
        pageNumberInputRef.current?.select();
      }, 50);
    }
  }, [isEditingPageNumber]);

  const handlePrevious = () => {
    if (currentIndex > 0 && onIndexChange) {
      onIndexChange(currentIndex - 1);
      // Scroll image container to top
      if (imageContainerRef.current) {
        imageContainerRef.current.scrollTop = 0;
      }
    }
  };

  const handleNext = () => {
    if (currentIndex < chunkUrls.length - 1 && onIndexChange) {
      onIndexChange(currentIndex + 1);
      // Scroll image container to top
      if (imageContainerRef.current) {
        imageContainerRef.current.scrollTop = 0;
      }
    }
  };

  const toggleItemExpanded = (index) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const formatValue = (value, key = "") => {
    return formatPropertyPageValue(value, key);
  };

  const handlePageNumberChange = (event) => {
    const pageNum = parseInt(event.target.value, 10);

    // Validate and clamp to bounds
    let targetIndex;
    if (isNaN(pageNum) || pageNum < 1) {
      targetIndex = 0;
    } else if (pageNum > chunkUrls.length) {
      targetIndex = chunkUrls.length - 1;
    } else {
      targetIndex = pageNum - 1; // Convert 1-based to 0-based index
    }

    // Navigate to the page
    if (onIndexChange && targetIndex !== currentIndex) {
      onIndexChange(targetIndex);
      // Scroll image container to top
      if (imageContainerRef.current) {
        imageContainerRef.current.scrollTop = 0;
      }
    }

    // Close the edit mode
    setIsEditingPageNumber(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "90vh",
          height: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Document Preview</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ display: "flex", gap: 2, p: 0, overflow: "hidden" }}>
        {/* Left side - Image (takes up 2/3 of width) */}
        <Box
          ref={imageContainerRef}
          sx={{
            flex: "0 0 66%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "flex-start",
            p: 2,
            overflow: "auto",
          }}
        >
          <img
            src={imageUrl}
            alt="Chunk preview"
            style={{
              maxWidth: "100%",
              height: "auto",
              display: "block",
            }}
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "block";
            }}
          />
          <Typography
            variant="body2"
            color="error"
            sx={{
              display: "none",
              p: 2,
            }}
          >
            Image not available
          </Typography>
        </Box>

        {/* Right side - Memory Items (takes up 1/3 of width) */}
        <Box
          sx={{
            flex: "0 0 34%",
            borderLeft: "1px solid",
            borderColor: "divider",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" sx={{ fontSize: "14px", fontWeight: 600 }}>
              In this section
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Page {currentIndex + 1} • Chunk {currentIndex + 1}
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            {isLoadingMemory ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <CircularProgress size={24} />
              </Box>
            ) : memoryItems.length > 0 ? (
              <List disablePadding>
                {memoryItems.map((item, index) => {
                  const isExpanded = expandedItems[index] || false;
                  const itemName = item.name || item.Name || item.object || item.Object || `Item ${index + 1}`;

                  // Get all properties excluding internal ones (same as JsonDocumentViewer)
                  const properties = Object.entries(item)
                    .filter(([key]) => !key.startsWith("_") && !EXCLUDED_PROPERTIES.has(key))
                    .sort(([keyA], [keyB]) => keyA.toLowerCase().localeCompare(keyB.toLowerCase()));

                  return (
                    <Paper key={index} elevation={1} sx={{ mb: 2 }}>
                      {/* Header - Always visible */}
                      <Box
                        sx={{
                          p: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          bgcolor: "action.selected",
                          borderRadius: "4px",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                        }}
                        onClick={() => toggleItemExpanded(index)}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "13px", mb: 0.5 }}>
                            {itemName}
                          </Typography>
                          {!isExpanded && (item.value || item.Value) && (
                            <Typography variant="body2" sx={{ fontSize: "12px", color: "text.secondary" }}>
                              {formatValue(item.value || item.Value, "value")}
                            </Typography>
                          )}
                        </Box>
                        <IconButton size="small" sx={{ pointerEvents: "none" }}>
                          {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                      </Box>

                      {/* Expandable content */}
                      <Collapse in={isExpanded}>
                        <Box sx={{ p: 2, pt: 1 }}>
                          {properties.map(([key, value]) => (
                            <Box
                              key={key}
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "minmax(120px, 35%) 1fr",
                                gap: 2,
                                mb: 1,
                                pb: 1,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                "&:last-child": {
                                  borderBottom: "none",
                                  mb: 0,
                                  pb: 0,
                                },
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, fontSize: "12px", color: "text.secondary" }}
                              >
                                {formatSnakeCaseToUserFriendly(key)}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "12px", color: "text.primary", wordBreak: "break-word" }}
                              >
                                {formatValue(value, key)}
                              </Typography>
                            </Box>
                          ))}

                          {/* Show metadata if present */}
                          {item.metadata && Object.keys(item.metadata).filter((k) => !k.startsWith("_")).length > 0 && (
                            <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 1 }}>
                                Metadata
                              </Typography>
                              {Object.entries(item.metadata)
                                .filter(([key]) => !key.startsWith("_"))
                                .sort(([keyA], [keyB]) => keyA.toLowerCase().localeCompare(keyB.toLowerCase()))
                                .map(([key, value]) => (
                                  <Box
                                    key={key}
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: "minmax(120px, 35%) 1fr",
                                      gap: 2,
                                      mb: 0.5,
                                    }}
                                  >
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                      {formatSnakeCaseToUserFriendly(key)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "text.primary" }}>
                                      {formatValue(value, key)}
                                    </Typography>
                                  </Box>
                                ))}
                            </Box>
                          )}
                        </Box>
                      </Collapse>
                    </Paper>
                  );
                })}
              </List>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                <Typography variant="body2" color="text.secondary">
                  No memory items for this chunk
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        {showNavigation ? (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <IconButton onClick={handlePrevious} disabled={currentIndex <= 0} size="small" aria-label="Previous image">
              <ChevronLeft />
            </IconButton>
            {isEditingPageNumber ? (
              <StatefulControlledTextField
                defaultValue={currentIndex + 1}
                onChange={handlePageNumberChange}
                maskType={MASK_NUMBER_UNFORMATTED}
                size="small"
                variant="outlined"
                inputRef={pageNumberInputRef}
                endAdornment={
                  <Typography variant="body2" sx={{ fontSize: "0.875rem", color: "text.secondary", ml: 0.5 }}>
                    / {chunkUrls.length}
                  </Typography>
                }
                inputProps={{
                  allowNegative: false,
                  sx: {
                    textAlign: "right",
                    padding: "4px 8px",
                    fontSize: "0.875rem",
                  },
                  onKeyDown: (e) => {
                    if (e.key === "Escape") {
                      setIsEditingPageNumber(false);
                    }
                  },
                }}
                sx={{
                  minWidth: "100px",
                  maxWidth: "100px",
                  "& .MuiOutlinedInput-root": {
                    paddingRight: "8px",
                    "& fieldset": {
                      borderWidth: "1px",
                      borderColor: "divider",
                    },
                    "&:hover fieldset": {
                      borderColor: "text.secondary",
                    },
                    "&.Mui-focused fieldset": {
                      borderWidth: "1px",
                      borderColor: "primary.main",
                    },
                  },
                  "& .MuiInputBase-input": {
                    textAlign: "right",
                    padding: "4px 8px",
                    fontSize: "0.875rem",
                  },
                }}
                showErrorSpace={false}
              />
            ) : (
              <Typography
                variant="body2"
                onClick={() => setIsEditingPageNumber(true)}
                sx={{
                  minWidth: "60px",
                  textAlign: "center",
                  cursor: "pointer",
                  padding: "4px 8px",
                  border: "1px solid",
                  borderColor: "transparent",
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "divider",
                    bgcolor: "action.hover",
                  },
                }}
              >
                {currentIndex + 1} / {chunkUrls.length}
              </Typography>
            )}
            <IconButton
              onClick={handleNext}
              disabled={currentIndex >= chunkUrls.length - 1}
              size="small"
              aria-label="Next image"
            >
              <ChevronRight />
            </IconButton>
          </Box>
        ) : (
          <Box />
        )}
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChunkImageDialog;
